import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { FirstPackageClientEntry } from '../types';
import { ConversionSegment, SEGMENT_LABELS, mayStillConvert, renewalText, segmentOf, unusedSessions } from '../utils/conversionSegments';
import { shortPricingOption } from './ClientMovementCard';

/** The chart legend says "Dropped off mid-package"; the chips and outcome cells use the shorter label. */
export const TABLE_LABELS: Record<ConversionSegment, string> = { ...SEGMENT_LABELS, dropped: 'Dropped off' };

const OUTCOME_CHIP_COLORS: Record<ConversionSegment, { bg: string; text: string }> = {
  converted: { bg: '#DBEAFE', text: '#1E40AF' },
  finished: { bg: '#FFEDD5', text: '#9A3412' },
  dropped: { bg: '#CCFBF1', text: '#115E59' },
};

const SessionsText: React.FC<{ used: number; purchased: number; color: string }> = ({ used, purchased, color }) => (
  <Box component="span" sx={{ color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
    {`${used} of ${purchased}`}
  </Box>
);

/**
 * Sessions used of those bought. Used up, the count sits quietly in grey, as most rows do; with sessions left it is
 * in full-strength text, and the outcome cell names how many were unused.
 */
export const SessionsCell: React.FC<{ entry: FirstPackageClientEntry }> = ({ entry }) => {
  const { sessionsUsed: used, sessionsPurchased: purchased } = entry;
  if (used === null || purchased === null) return <>—</>;
  return <SessionsText used={used} purchased={purchased} color={used >= purchased ? 'text.secondary' : 'text.primary'} />;
};

const outcomeDetail = (entry: FirstPackageClientEntry, segment: ConversionSegment): string | null => {
  if (segment === 'converted') {
    const next = entry.nextPricingOption ? shortPricingOption(entry.nextPricingOption) : null;
    return [next, renewalText(entry.daysExpiryToNextPackage)].filter(Boolean).join(' · ') || null;
  }
  if (segment === 'dropped') {
    const unused = unusedSessions(entry);
    if (unused !== null && unused > 0) return `${unused} ${unused === 1 ? 'session' : 'sessions'} unused`;
  }
  return null;
};

/** The outcome chip, the detail beneath it (two lines at most, the whole of it on hover), and the may-still-convert marker. */
export const OutcomeCell: React.FC<{ entry: FirstPackageClientEntry; sourceCreatedDate: string }> = ({ entry, sourceCreatedDate }) => {
  const segment = segmentOf(entry);
  const colors = OUTCOME_CHIP_COLORS[segment];
  const detail = outcomeDetail(entry, segment);
  return (
    <Box>
      <Chip size="small" label={TABLE_LABELS[segment]} sx={{ fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />
      {detail ? (
        <Typography
          variant="caption"
          component="div"
          title={detail}
          sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {detail}
        </Typography>
      ) : null}
      {mayStillConvert(entry, sourceCreatedDate) ? (
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          May still convert
        </Typography>
      ) : null}
    </Box>
  );
};
